import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { FiMessageSquare, FiBook, FiSettings, FiLogOut, FiUser, FiHome, FiUsers, FiFolder } from 'react-icons/fi';

function Dashboard() {
  const { user, logout } = useAuth();
  const { unreadCount, mentionUnreadCount } = useNotifications();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="pb-8">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 drop-shadow-lg">
            Welcome back, {user?.firstName || user?.username}!
          </h2>
          <p className="text-white/90 text-sm sm:text-base">Choose an option below to get started</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {/* Chat Card */}
          <div
            onClick={() => navigate('/chat')}
            className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-[#3c79bb]"
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="bg-[#3c79bb]/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-[#3c79bb] transition-all duration-300 group-hover:shadow-lg group-hover:shadow-[#3c79bb]/30">
                <FiMessageSquare className="text-[#3c79bb] group-hover:text-white text-xl sm:text-2xl transition-colors" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex-1 flex items-center gap-2 min-w-0">
                <span className="truncate">Team Chat</span>
                <span className="flex flex-shrink-0 items-center gap-1">
                  {mentionUnreadCount > 0 && (
                    <span className="min-w-[1.25rem] h-5 px-1 inline-flex items-center justify-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full">
                      @{mentionUnreadCount > 99 ? '99+' : mentionUnreadCount}
                    </span>
                  )}
                  {unreadCount > 0 && (
                    <span className="min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
              </h3>
            </div>
            <p className="text-gray-800 text-sm sm:text-base leading-relaxed font-medium">
              Communicate with your team in real-time. Share messages and stay connected.
            </p>
          </div>

          {/* Training Card */}
          <div
            onClick={() => navigate('/training')}
            className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-green-500"
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="bg-green-500/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-green-500 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-green-500/30">
                <FiBook className="text-green-600 group-hover:text-white text-xl sm:text-2xl transition-colors" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Training</h3>
            </div>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              Access training modules and track your progress. Complete courses at your own pace.
            </p>
          </div>

          {/* Files Card */}
          <div
            onClick={() => navigate('/files')}
            className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-amber-500"
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="bg-amber-500/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-amber-500 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-amber-500/30">
                <FiFolder className="text-amber-600 group-hover:text-white text-xl sm:text-2xl transition-colors" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Files</h3>
            </div>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              Store and organize important documents. Upload files and create folders for easy access.
            </p>
          </div>

          {/* Admin Training Management */}
          {user?.role === 'admin' && (
            <>
              <div
                onClick={() => navigate('/admin/training')}
                className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-purple-500"
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="bg-purple-500/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-purple-500 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-purple-500/30">
                    <FiSettings className="text-purple-600 group-hover:text-white text-xl sm:text-2xl transition-colors" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Manage Training</h3>
                </div>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Create, edit, and manage training modules for your team.
                </p>
              </div>
              <div
                onClick={() => navigate('/admin/users')}
                className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-orange-500"
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="bg-orange-500/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-orange-500 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-orange-500/30">
                    <FiUsers className="text-orange-600 group-hover:text-white text-xl sm:text-2xl transition-colors" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Manage Users</h3>
                </div>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Add staff members, edit profiles, and manage user accounts.
                </p>
              </div>
            </>
          )}
          
          {/* Profile Card */}
          <div
            onClick={() => navigate('/profile')}
            className="glass-card rounded-2xl p-5 sm:p-6 cursor-pointer hover:glass-strong hover:scale-[1.02] transition-all duration-300 group border-l-4 border-indigo-500"
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="bg-indigo-500/20 p-2.5 sm:p-3 rounded-xl group-hover:bg-indigo-500 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-indigo-500/30">
                <FiUser className="text-indigo-600 group-hover:text-white text-xl sm:text-2xl transition-colors" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">My Profile</h3>
            </div>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              View and edit your profile information and settings.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
